import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { CardField } from "src/utils/card/cardData";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";

interface CanScaleFormData {
  CS_ID: string;
  CUSER: string;
  CTIME: Date;

  HAIR: string | null;
  CHEEK: string | null;
  CHIN_AND_NECK: string | null;
  ARM: string | null;
  BACK: string | null;
  HIP: string | null;
  LEG: string | null;
  RIB_CAGE: string | null;
  ABDOMINAL_SKIN: string | null;

  SCORE: number | null;
  NOTE: string;
}

interface CanScaleProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof CanScaleFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function CanScaleCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: CanScaleProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    getScoreValue,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "CAN_SCALE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof CanScaleFormData, value);
      });
    },
  });

  const typedFormData = formData as CanScaleFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof CanScaleFormData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;

    const rowData: any = {
      HAIR_XVALUE: getScoreValue("HAIR"),
      CHEEK_XVALUE: getScoreValue("CHEEK"),
      CHIN_AND_NECK_XVALUE: getScoreValue("CHIN_AND_NECK"),
      ARM_XVALUE: getScoreValue("ARM"),
      BACK_XVALUE: getScoreValue("BACK"),
      HIP_XVALUE: getScoreValue("HIP"),
      LEG_XVALUE: getScoreValue("LEG"),
      RIB_CAGE_XVALUE: getScoreValue("RIB_CAGE"),
      ABDOMINAL_SKIN_XVALUE: getScoreValue("ABDOMINAL_SKIN"),
    };

    const canScaleScoreField = cardData.fields.find(
      (f: any) => f.field === "score"
    ) as CardField | undefined;
    if (canScaleScoreField?.calculate) {
      const calculatedScore = canScaleScoreField.calculate(rowData);
      setScore(calculatedScore);
      parentHandleChange("SCORE", calculatedScore);
    }
  }, [
    cardData,
    getScoreValue,
    parentHandleChange,
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate]);


  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number,
    readOnly: boolean = false
  ) => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6}>
        {readOnly ? (
          <ByzMuiTextField
            sx={{ width: "100%" }}
            color="info"
            variant="outlined"
            value={typedFormData[fieldName as keyof CanScaleFormData] || ""}
            disabled
          />
        ) : (
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={mcpId}
            multiple={false}
            handleChange={(val) => {
              if (val === null || val === undefined) {
                // Clear butonuna basıldığında null gönder
                handleMCPChange(fieldName, null);
              } else if (!Array.isArray(val)) {
                const selected = val as McParameterDetailResponseDto;
                handleMCPChange(fieldName, selected);
              }
            }}
            value={selectedMCPs[fieldName]}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(mcpId)}
            cacheLoaded={mcpCacheLoaded}
          />
        )}
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ padding: "20px", width: "100%" }}>
      <Box mb={3}>
        <OtherPrivilegeGuard
          extraAccess={!!extraAccess}
          fallback={
            <ByzMuiSaveButton
              disabled
              tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
            />
          }
        >
          <ByzMuiSaveButton
            onClick={() => save(typedFormData.CS_ID)}
            loading={isSaving}
          />
        </OtherPrivilegeGuard>
      </Box>
      <Grid container spacing={2}>
        <Grid md={6} xs={6} container direction={"column"} spacing={3}>
          <Grid md={6} xs={6} sx={{ backgroundColor: "#F4F7FE" }}>
            <ByzMuiTypography isTitle>{t("can-scale.title")}</ByzMuiTypography>
          </Grid>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("datetime")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6} sx={{ width: "100%" }}>
              <ByzMuiDateTimePicker
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
                label={t("datetime")}
                value={typedFormData.CTIME}
                onChange={(date) => handleChange("CTIME", date)}
                disabled={!extraAccess}
              />
            </Grid>
          </Grid>

          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              xs={12}
              sm={6}
              md={6}
              sx={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("createdby")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6}>
              <ByzMuiTextField
                sx={{ width: "100%" }}
                color="info"
                label={t("createdby")}
                variant="outlined"
                value={typedFormData.CUSER}
                onChange={(e) => handleChange("CUSER", e.target.value)}
                disabled
              />
            </Grid>
          </Grid>

          <Grid md={6} xs={6} sx={{ backgroundColor: "#F4F7FE" }}>
            <ByzMuiTypography isTitle>
              {t("can-scale.parameters")}
            </ByzMuiTypography>
          </Grid>

          {renderMCPField("HAIR", "can-scale.hair", 607, !extraAccess)}
          {renderMCPField("CHEEK", "can-scale.cheek", 608, !extraAccess)}
          {renderMCPField("CHIN_AND_NECK", "can-scale.chin-and-neck", 609, !extraAccess)}
          {renderMCPField("ARM", "can-scale.arm", 610, !extraAccess)}
          {renderMCPField("BACK", "can-scale.back", 611, !extraAccess)}
          {renderMCPField("HIP", "can-scale.hip", 612, !extraAccess)}
          {renderMCPField("LEG", "can-scale.leg", 613, !extraAccess)}
          {renderMCPField("RIB_CAGE", "can-scale.rib-cage", 614, !extraAccess)}
          {renderMCPField("ABDOMINAL_SKIN", "can-scale.abdominal-skin", 615, !extraAccess)}

          <Grid md={6} xs={6} sx={{ backgroundColor: "#F4F7FE" }}>
            <ByzMuiTypography isTitle>{t("can-scale.other")}</ByzMuiTypography>
          </Grid>

          <Grid
            container
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("can-scale.score")}
              </ByzMuiTypography>
            </Grid>

            <Grid md={6} xs={6}>
              <ByzMuiTextField
                sx={{ width: "100%" }}
                color="info"
                disabled
                value={score}
                label={t("can-scale.score")}
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Grid
            container
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("can-scale.note")}
              </ByzMuiTypography>
            </Grid>

            <Grid md={6} xs={6}>
              <ByzMuiTextField
                sx={{ width: "100%" }}
                color="info"
                label={t("can-scale.note")}
                variant="outlined"
                onChange={(e) => handleChange("NOTE", e.target.value)}
                value={typedFormData.NOTE}
                disabled={!extraAccess}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CanScaleCreate;
